import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../contexts/AuthContext'
import {
  printables as printablesApi,
  notebooks  as notebooksApi,
  userNotebooks as userNotebooksApi,
  themes as themesApi,
  type PrintableSummary,
  type NotebookSummary,
  type PrintableType,
  type ThemeSummary,
} from '../api/client'
import { useMobile } from '../hooks/useMobile'
import { useTranslation } from '../i18n/LanguageContext'
import Footer from './Footer'
import {
  useNotebookItemsBuilder,
  NotebookItemsList,
  NotebookActivityForm,
} from './NotebookItemsBuilder'

// ── Constants ─────────────────────────────────────────────────────────────────

const TYPE_ICONS: Record<PrintableType, string> = {
  memory_cards:     '🃏',
  memory_reference: '📋',
  definitions:      '🔍',
  anagram:          '🔤',
}

const TIER_COLORS: Record<string, string> = {
  BASIC:   '#4CAF50',
  PLUS:    '#2196F3',
  PREMIUM: '#9C27B0',
}

// ── Download helpers ──────────────────────────────────────────────────────────

async function downloadPrintable(id: number, title: string) {
  const { url } = await printablesApi.download(id)
  triggerDownload(url, `${title}.pdf`)
}

async function downloadPublicNotebook(id: number, title: string) {
  const { url } = await notebooksApi.download(id)
  triggerDownload(url, `${title}.pdf`)
}

async function downloadUserNotebook(id: number, title: string) {
  const { url } = await userNotebooksApi.download(id)
  triggerDownload(url, `${title}.pdf`)
}

function triggerDownload(url: string, filename: string) {
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.target = '_blank'
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
}

// ── User Notebook Builder Modal ───────────────────────────────────────────────

interface UserNotebookBuilderModalProps {
  language: string
  onClose:   () => void
  onCreated: (n: NotebookSummary) => void
}

function UserNotebookBuilderModal({ language, onClose, onCreated }: UserNotebookBuilderModalProps) {
  const { t } = useTranslation()
  const dl = t.downloads
  const games = t.dashboard.games as Record<string, { label: string }>

  const [wsThemes, setWsThemes] = useState<ThemeSummary[]>([])
  const [atThemes, setAtThemes] = useState<ThemeSummary[]>([])

  useEffect(() => {
    themesApi.list('wordsearch').then(setWsThemes).catch(() => {})
    themesApi.list('attention').then(setAtThemes).catch(() => {})
  }, [])

  const builder = useNotebookItemsBuilder({
    language,
    wordsearchThemes: wsThemes,
    attentionThemes: atThemes,
    diffLabels: dl.diffLabels,
    games,
  })

  const [title, setTitle] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleGenerate() {
    if (!title.trim()) { setError('El título es obligatorio'); return }
    if (builder.items.length === 0) { setError('Añade al menos una actividad'); return }
    setSaving(true); setError(null)
    try {
      const created = await userNotebooksApi.create({
        title: title.trim(),
        language,
        items: builder.items.map(i => i.spec),
      })
      onCreated(created)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Error al crear')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div style={modalOverlay} onClick={onClose}>
      <div style={{ ...modalBox, maxWidth: 520, maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
        <h2 style={modalTitle}>{dl.builderTitle}</h2>

        <label style={modalLabel}>Título</label>
        <input
          style={modalInput}
          value={title}
          onChange={e => setTitle(e.target.value)}
          placeholder="Ej. Mi cuaderno de primavera"
          autoFocus
        />

        {/* Item list */}
        <label style={modalLabel}>{dl.builderActivities} ({builder.items.length})</label>
        <NotebookItemsList
          items={builder.items}
          onRemove={builder.removeItem}
          onMoveUp={builder.moveUp}
          onMoveDown={builder.moveDown}
          showReorder
          emptyLabel={language === 'en'
            ? 'Use the buttons below to add activities.'
            : 'Usa los botones de abajo para añadir actividades.'}
        />

        {/* Activity form */}
        <NotebookActivityForm
          hook={builder}
          language={language}
          wordsearchThemes={wsThemes}
          attentionThemes={atThemes}
          labelStyle={modalLabel}
          inputStyle={modalInput}
          btnPrimaryStyle={modalBtnPrimary}
        />

        {error && <p style={{ fontSize: '0.875rem', color: '#C62828', margin: '8px 0 0' }}>{error}</p>}

        <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
          <button style={{ ...modalBtnSecondary, flex: 1 }} onClick={onClose} disabled={saving}>Cancelar</button>
          <button style={{ ...modalBtnPrimary, flex: 2 }} onClick={handleGenerate} disabled={saving || builder.items.length === 0}>
            {saving ? dl.builderGenerating : dl.builderGenerate}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Card components ───────────────────────────────────────────────────────────

interface PrintableCardProps {
  p: PrintableSummary & { locked: boolean }
  onGoToSubscription: () => void
}

function PrintableCard({ p, onGoToSubscription }: PrintableCardProps) {
  const { t } = useTranslation()
  const dl = t.downloads
  const [downloading, setDownloading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleDownload() {
    setDownloading(true); setError(null)
    try {
      await downloadPrintable(p.id, p.title)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : dl.errorDownload)
    } finally {
      setDownloading(false)
    }
  }

  return (
    <div style={cardStyles.card}>
      <div style={cardStyles.iconRow}>
        <span style={cardStyles.icon}>{TYPE_ICONS[p.type]}</span>
        <span style={{ ...cardStyles.tierBadge, background: TIER_COLORS[p.requiredTier] }}>
          {dl.tierLabels[p.requiredTier as keyof typeof dl.tierLabels] ?? p.requiredTier}
        </span>
      </div>
      <h3 style={cardStyles.title}>{p.title}</h3>
      <p style={cardStyles.meta}>
        {dl.typeLabels[p.type as keyof typeof dl.typeLabels] ?? p.type} · {dl.diffLabels[p.difficulty as keyof typeof dl.diffLabels] ?? p.difficulty} · {p.language.toUpperCase()}
      </p>
      {error && <p style={cardStyles.error}>{error}</p>}
      {p.locked ? (
        <button style={cardStyles.lockedBtn} onClick={onGoToSubscription}>{dl.upgradeBtn}</button>
      ) : !p.hasFile ? (
        <span style={cardStyles.notReady}>{dl.notReady}</span>
      ) : (
        <button style={cardStyles.downloadBtn} onClick={handleDownload} disabled={downloading}>
          {downloading ? dl.downloading : dl.downloadBtn}
        </button>
      )}
    </div>
  )
}

interface NotebookCardProps {
  n: NotebookSummary & { locked: boolean }
  onGoToSubscription: () => void
}

function NotebookCard({ n, onGoToSubscription }: NotebookCardProps) {
  const { t } = useTranslation()
  const dl = t.downloads
  const [downloading, setDownloading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleDownload() {
    setDownloading(true); setError(null)
    try {
      await downloadPublicNotebook(n.id, n.title)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : dl.errorDownload)
    } finally {
      setDownloading(false)
    }
  }

  return (
    <div style={{ ...cardStyles.card, borderColor: '#C8B8D8' }}>
      <div style={cardStyles.iconRow}>
        <span style={cardStyles.icon}>📓</span>
        <span style={{ ...cardStyles.tierBadge, background: TIER_COLORS[n.requiredTier] }}>
          {dl.tierLabels[n.requiredTier as keyof typeof dl.tierLabels] ?? n.requiredTier}
        </span>
      </div>
      <h3 style={cardStyles.title}>{n.title}</h3>
      <p style={cardStyles.meta}>
        {dl.notebookMeta.replace('{n}', String(n.itemCount ?? 0))}
        {n.activities && n.activities.length > 0 && ` · ${n.activities.join(', ')}`}
      </p>
      {error && <p style={cardStyles.error}>{error}</p>}
      {n.locked ? (
        <button style={cardStyles.lockedBtn} onClick={onGoToSubscription}>{dl.upgradeBtn}</button>
      ) : !n.hasFile ? (
        <span style={cardStyles.notReady}>{dl.notReady}</span>
      ) : (
        <button style={cardStyles.downloadBtn} onClick={handleDownload} disabled={downloading}>
          {downloading ? dl.downloading : dl.downloadBtn}
        </button>
      )}
    </div>
  )
}

interface UserNotebookCardProps {
  n:          NotebookSummary
  onDelete:   (id: number) => void
  onRetry:    (id: number) => void
  deleting:   boolean
}

function UserNotebookCard({ n, onDelete, onRetry, deleting }: UserNotebookCardProps) {
  const { t } = useTranslation()
  const dl = t.downloads
  const [downloading, setDownloading] = useState(false)
  const [error,       setError]       = useState<string | null>(null)

  async function handleDownload() {
    setDownloading(true); setError(null)
    try {
      await downloadUserNotebook(n.id, n.title)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : dl.errorDownload)
    } finally {
      setDownloading(false)
    }
  }

  const isGenerating = n.generationStatus === 'GENERATING'
  const isFailed     = n.generationStatus === 'FAILED'
  const isDone       = n.generationStatus === 'DONE'

  return (
    <div style={{ ...cardStyles.card, borderColor: '#B8C8D8' }}>
      <div style={cardStyles.iconRow}>
        <span style={cardStyles.icon}>📔</span>
        <span style={{ ...cardStyles.tierBadge, background: '#555566' }}>
          {n.language.toUpperCase()}
        </span>
      </div>
      <h3 style={cardStyles.title}>{n.title}</h3>
      <p style={cardStyles.meta}>
        {dl.notebookMeta.replace('{n}', String(n.pageCount ?? 0))}
      </p>
      {error && <p style={cardStyles.error}>{error}</p>}

      {isGenerating && (
        <span style={{ ...cardStyles.notReady, color: '#FF9800' }}>{dl.statusGenerating}</span>
      )}
      {isFailed && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span style={{ ...cardStyles.notReady, color: '#C62828' }}>{dl.statusFailed}</span>
          {n.generationError && (
            <span style={{ fontSize: '0.875rem', color: '#C62828' }} title={n.generationError}>⚠ Error</span>
          )}
          <button style={cardStyles.downloadBtn} onClick={() => onRetry(n.id)}>
            {dl.retryBtn}
          </button>
        </div>
      )}
      {isDone && (
        <button style={cardStyles.downloadBtn} onClick={handleDownload} disabled={downloading}>
          {downloading ? dl.downloading : dl.downloadBtn}
        </button>
      )}

      <button
        style={{ ...cardStyles.lockedBtn, marginTop: 4, fontSize: '0.875rem', color: '#C62828' }}
        onClick={() => { if (confirm(`¿Eliminar "${n.title}"?`)) onDelete(n.id) }}
        disabled={deleting}
      >
        {dl.deleteBtn}
      </button>
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────

interface DownloadsPageProps {
  onBack: () => void
  onGoToSubscription?: () => void
}

export default function DownloadsPage({ onBack, onGoToSubscription }: DownloadsPageProps) {
  const { user } = useAuth()
  const isMobile = useMobile()
  const { t, language } = useTranslation()
  const dl = t.downloads

  const [tab,          setTab]        = useState<'all' | 'notebooks'>('all')
  const [pList,        setPList]      = useState<(PrintableSummary & { locked: boolean })[]>([])
  const [nList,        setNList]      = useState<(NotebookSummary & { locked: boolean })[]>([])
  const [myList,       setMyList]     = useState<NotebookSummary[]>([])
  const [loading,      setLoading]    = useState(true)
  const [error,        setError]      = useState<string | null>(null)
  const [showBuilder,  setShowBuilder] = useState(false)
  const [deletingIds,  setDeletingIds] = useState<Set<number>>(new Set())

  const isPremium = user?.tier === 'PREMIUM'

  const visiblePList    = pList.filter(p => p.language === language)
  const visibleNList    = nList.filter(n => n.language === language)
  const visibleMyList   = myList.filter(n => n.language === language)

  function goToSubscription() {
    onGoToSubscription?.()
  }

  const loadErrorMsg = dl.errorDownload

  const loadAll = useCallback(() => {
    const fetches: [Promise<(PrintableSummary & { locked: boolean })[]>, Promise<(NotebookSummary & { locked: boolean })[]>, Promise<NotebookSummary[]>] = [
      printablesApi.list(),
      notebooksApi.list(),
      userNotebooksApi.list(),
    ]
    Promise.all(fetches)
      .then(([ps, ns, my]) => { setPList(ps); setNList(ns); setMyList(my) })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : loadErrorMsg))
      .finally(() => setLoading(false))
  }, [loadErrorMsg])

  useEffect(() => { loadAll() }, [loadAll])

  // Poll while any user notebook is generating
  useEffect(() => {
    if (!myList.some(n => n.generationStatus === 'GENERATING')) return
    const timer = setTimeout(() => {
      userNotebooksApi.list()
        .then(my => setMyList(my))
        .catch(() => {})
    }, 3000)
    return () => clearTimeout(timer)
  }, [myList])

  async function handleDelete(id: number) {
    setDeletingIds(prev => new Set(prev).add(id))
    try {
      await userNotebooksApi.delete(id)
      setMyList(prev => prev.filter(n => n.id !== id))
    } catch {
      // silent — user will see the notebook still there
    } finally {
      setDeletingIds(prev => { const s = new Set(prev); s.delete(id); return s })
    }
  }

  async function handleRetry(id: number) {
    try {
      await userNotebooksApi.generate(id)
      setMyList(prev => prev.map(n => n.id === id ? { ...n, generationStatus: 'GENERATING' as const } : n))
    } catch { /* silent */ }
  }

  function handleNotebookCreated(n: NotebookSummary) {
    setMyList(prev => [n, ...prev])
    setShowBuilder(false)
  }

  const baseGrid: React.CSSProperties = {
    display:             'grid',
    gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(260px, 1fr))',
    gap:                 16,
  }

  return (
    <div style={pageStyles.page}>
      <header style={pageStyles.header}>
        <button onClick={onBack} style={pageStyles.backBtn}>{t.profile.backBtn}</button>
        <h1 style={pageStyles.headerTitle}>{dl.title}</h1>
        {user && (
          <span style={pageStyles.tierTag}>{user.tier}</span>
        )}
      </header>

      <div style={{ ...pageStyles.content, maxWidth: isMobile ? '100%' : 960 }}>
        <p style={pageStyles.intro}>{dl.intro}</p>

        {/* Tab bar */}
        <div style={pageStyles.tabBar}>
          <button
            style={{ ...pageStyles.tab, ...(tab === 'all' ? pageStyles.tabActive : {}) }}
            onClick={() => setTab('all')}
          >
            {dl.printablesTab} ({visiblePList.length})
          </button>
          <button
            style={{ ...pageStyles.tab, ...(tab === 'notebooks' ? pageStyles.tabActive : {}) }}
            onClick={() => setTab('notebooks')}
          >
            {dl.notebooksTab} ({visibleNList.length + visibleMyList.length})
          </button>
        </div>

        {error && <p style={{ color: '#F44336', marginBottom: 12 }}>{error}</p>}

        {loading ? (
          <p style={{ textAlign: 'center', color: '#888', padding: 40 }}>{dl.loading}</p>
        ) : tab === 'all' ? (

          // ── Printables tab ──────────────────────────────────────────────────
          visiblePList.length === 0 ? (
            <p style={pageStyles.empty}>{dl.emptyPrintables}</p>
          ) : (
            <div style={baseGrid}>
              {visiblePList.map(p => (
                <PrintableCard key={p.id} p={p} onGoToSubscription={goToSubscription} />
              ))}
            </div>
          )

        ) : (

          // ── Notebooks tab ───────────────────────────────────────────────────
          <div>

            {/* Generate my notebook CTA */}
            <div style={ctaBox}>
              {isPremium ? (
                <button style={ctaBtn} onClick={() => setShowBuilder(true)}>
                  {dl.generateNotebookBtn}
                </button>
              ) : (
                <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: 'center', gap: 12 }}>
                  <p style={{ margin: 0, fontSize: '0.9rem', color: '#4A1970', flex: 1 }}>
                    {dl.premiumRequired}
                  </p>
                  <button style={upgradeBtn} onClick={goToSubscription}>
                    {dl.upgradeToPremiumBtn}
                  </button>
                </div>
              )}
            </div>

            {/* My Notebooks subsection */}
            {(isPremium || visibleMyList.length > 0) && (
              <section style={{ marginBottom: 32 }}>
                <h2 style={sectionTitle}>{dl.myNotebooksTitle}</h2>
                {visibleMyList.length === 0 ? (
                  <p style={pageStyles.empty}>{dl.emptyMyNotebooks}</p>
                ) : (
                  <div style={baseGrid}>
                    {visibleMyList.map(n => (
                      <UserNotebookCard
                        key={n.id}
                        n={n}
                        onDelete={handleDelete}
                        onRetry={handleRetry}
                        deleting={deletingIds.has(n.id)}
                      />
                    ))}
                  </div>
                )}
              </section>
            )}

            {/* Library subsection */}
            <section>
              <h2 style={sectionTitle}>{dl.libraryTitle}</h2>
              {visibleNList.length === 0 ? (
                <p style={pageStyles.empty}>{dl.emptyNotebooks}</p>
              ) : (
                <div style={baseGrid}>
                  {visibleNList.map(n => (
                    <NotebookCard key={n.id} n={n} onGoToSubscription={goToSubscription} />
                  ))}
                </div>
              )}
            </section>

          </div>
        )}
      </div>

      {showBuilder && (
        <UserNotebookBuilderModal
          language={language}
          onClose={() => setShowBuilder(false)}
          onCreated={handleNotebookCreated}
        />
      )}
      <Footer />
    </div>
  )
}

// ── Styles ────────────────────────────────────────────────────────────────────

const pageStyles: Record<string, React.CSSProperties> = {
  page:    { minHeight: '100vh', display: 'flex', flexDirection: 'column', background: '#FAF6EF' },
  header:  {
    background:  'linear-gradient(135deg, #1B4332, #2D6A4F)',
    padding:     '16px 24px',
    display:     'flex',
    alignItems:  'center',
    gap:         12,
  },
  backBtn: {
    fontSize:     '0.9rem',
    fontWeight:   700,
    color:        'rgba(255,255,255,0.8)',
    background:   'rgba(255,255,255,0.12)',
    border:       'none',
    borderRadius: 10,
    padding:      '8px 14px',
    cursor:       'pointer',
    minHeight:    40,
  },
  headerTitle: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize:   '1.6rem',
    fontWeight: 700,
    color:      '#FFFFFF',
    margin:     0,
    flex:       1,
  },
  tierTag: {
    fontSize: '0.875rem',
    fontWeight:   700,
    background:   'rgba(255,255,255,0.15)',
    color:        '#fff',
    borderRadius: 8,
    padding:      '4px 10px',
  },
  content: {
    flex:       1,
    padding:    '28px 20px',
    width:      '100%',
    margin:     '0 auto',
    boxSizing:  'border-box',
  },
  intro:   { color: '#6B5744', fontSize: '0.95rem', marginBottom: 20, lineHeight: 1.5 },
  tabBar:  { display: 'flex', gap: 4, marginBottom: 24, borderBottom: '2px solid #E8DDD0' },
  tab:     {
    padding: '10px 18px', border: 'none', background: 'transparent',
    cursor: 'pointer', fontSize: '0.9rem', color: '#888', borderRadius: '8px 8px 0 0',
  },
  tabActive: {
    background: '#fff', color: '#1B4332', fontWeight: 700,
    borderBottom: '2px solid #1B4332', marginBottom: -2,
  },
  empty: { textAlign: 'center', color: '#888', padding: '24px 0' },
}

const cardStyles: Record<string, React.CSSProperties> = {
  card: {
    background:    '#fff',
    border:        '1.5px solid #E8DDD0',
    borderRadius:  14,
    padding:       '20px 18px',
    display:       'flex',
    flexDirection: 'column',
    gap:           8,
  },
  iconRow:  { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  icon:     { fontSize: '2rem', lineHeight: 1 },
  tierBadge: {
    fontSize: '0.875rem',
    fontWeight:   700,
    color:        '#fff',
    borderRadius: 6,
    padding:      '2px 8px',
  },
  title: {
    fontFamily:   "'Cormorant Garamond', serif",
    fontSize:     '1.15rem',
    fontWeight:   700,
    color:        '#1A1A2E',
    margin:       0,
    lineHeight:   1.3,
  },
  meta:     { fontSize: '0.875rem', color: '#888', margin: 0 },
  error:    { fontSize: '0.875rem', color: '#C62828', margin: 0 },
  downloadBtn: {
    marginTop:    4,
    background:   '#1B4332',
    color:        '#fff',
    border:       'none',
    borderRadius: 10,
    padding:      '10px 0',
    cursor:       'pointer',
    fontWeight:   700,
    fontSize:     '0.9rem',
    minHeight:    44,
    width:        '100%',
  },
  lockedBtn: {
    marginTop:    4,
    background:   '#F0EAE0',
    color:        '#555',
    border:       'none',
    borderRadius: 10,
    padding:      '10px 0',
    cursor:       'pointer',
    fontWeight:   700,
    fontSize: '0.875rem',
    minHeight:    44,
    width:        '100%',
  },
  notReady: {
    marginTop:  4,
    fontSize: '0.875rem',
    color:      '#AAA',
    textAlign:  'center',
    padding:    '8px 0',
  },
}

const ctaBox: React.CSSProperties = {
  background:    'linear-gradient(135deg, #F3E8FF, #EDE0F8)',
  border:        '1.5px solid #D8B4FE',
  borderRadius:  14,
  padding:       '20px 22px',
  marginBottom:  28,
}

const ctaBtn: React.CSSProperties = {
  background:   '#7C3AED',
  color:        '#fff',
  border:       'none',
  borderRadius: 10,
  padding:      '12px 24px',
  cursor:       'pointer',
  fontWeight:   700,
  fontSize:     '0.95rem',
  minHeight:    48,
  width:        '100%',
}

const upgradeBtn: React.CSSProperties = {
  background:   '#7C3AED',
  color:        '#fff',
  border:       'none',
  borderRadius: 10,
  padding:      '10px 20px',
  cursor:       'pointer',
  fontWeight:   700,
  fontSize: '0.875rem',
  minHeight:    44,
  whiteSpace:   'nowrap',
}

const sectionTitle: React.CSSProperties = {
  fontSize:     '1rem',
  fontWeight:   700,
  color:        '#555566',
  marginBottom: 14,
  marginTop:    0,
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
}

// ── Modal shared styles ───────────────────────────────────────────────────────

const modalOverlay: React.CSSProperties = {
  position:       'fixed',
  inset:          0,
  background:     'rgba(0,0,0,0.45)',
  display:        'flex',
  alignItems:     'center',
  justifyContent: 'center',
  zIndex:         1000,
  padding:        16,
}

const modalBox: React.CSSProperties = {
  background:   '#FDFAF6',
  borderRadius: 16,
  padding:      '28px 24px',
  width:        '100%',
  boxShadow:    '0 8px 40px rgba(0,0,0,0.18)',
}

const modalTitle: React.CSSProperties = {
  fontFamily:   "'Cormorant Garamond', serif",
  fontSize:     '1.4rem',
  fontWeight:   700,
  color:        '#1B4332',
  margin:       '0 0 18px',
}

const modalLabel: React.CSSProperties = {
  display:      'block',
  fontSize: '0.875rem',
  fontWeight:   700,
  color:        '#555',
  marginBottom: 4,
  marginTop:    10,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
}

const modalInput: React.CSSProperties = {
  width:        '100%',
  boxSizing:    'border-box',
  border:       '1.5px solid #DDD',
  borderRadius: 8,
  padding:      '8px 10px',
  fontSize:     '0.9rem',
  background:   '#fff',
  marginBottom: 4,
}

const modalBtnPrimary: React.CSSProperties = {
  background:   '#1B4332',
  color:        '#fff',
  border:       'none',
  borderRadius: 10,
  padding:      '11px 0',
  cursor:       'pointer',
  fontWeight:   700,
  fontSize:     '0.9rem',
  minHeight:    44,
}

const modalBtnSecondary: React.CSSProperties = {
  background:   '#F0EAE0',
  color:        '#444',
  border:       'none',
  borderRadius: 10,
  padding:      '11px 0',
  cursor:       'pointer',
  fontWeight:   700,
  fontSize:     '0.9rem',
  minHeight:    44,
}
